import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, PackageSearch } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

const ProductsListing = () => {
  const { language } = useLanguage(); // 'en' | 'ta'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/q_commerce/data`);
        const result = await res.json();
        if (result?.products) {
          const mapped = result.products.map((p, i) => ({
            id: p.id ?? i,
            name_eng: p.product_name_english || '',
            name_tamil: p.product_name_tamil || '',
            image: `${MEDIA_BASE_URL}/${p.product_image}`,
            specs: p.quantity || p.weight || p.pack_size || null,
          }));
          setProducts(mapped);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name_eng.toLowerCase().includes(q) || p.name_tamil.includes(q)
    );
  }, [products, search]);

  const Skeleton = () => (
    <div
      className="bg-white overflow-hidden animate-pulse"
      style={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}
    >
      <div className="bg-gray-100 h-32 sm:h-40" />
      <div className="p-3 space-y-2">
        <div className="h-3.5 bg-gray-100 rounded-full w-4/5" />
        <div className="h-3 bg-gray-100 rounded-full w-3/5" />
      </div>
    </div>
  );

  const ProductCard = ({ product }) => {
    const [imgError, setImgError] = useState(false);

    return (
      <div
        className="bg-white overflow-hidden transition-shadow hover:shadow-md"
        style={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}
      >
        {/* Image */}
        <div className="w-full overflow-hidden flex items-center justify-center bg-gray-50 h-32 sm:h-40">
          {!imgError ? (
            <img
              src={product.image}
              alt={product.name_eng}
              onError={() => setImgError(true)}
              className="w-full h-full object-cover"
            />
          ) : (
            <PackageSearch size={32} className="text-gray-300" />
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <p
            className="text-gray-800 font-semibold leading-snug text-xs sm:text-sm"
          >
            {language === 'ta' ? product.name_tamil : product.name_eng}
          </p>
          {/* Secondary language label */}
          <p className="text-gray-400 mt-0.5 truncate text-[10px] sm:text-xs">
            {language === 'ta' ? product.name_eng : product.name_tamil}
          </p>
          {product.specs && (
            <p className="text-gray-500 mt-1.5 text-[10px] sm:text-xs">
              {product.specs}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: '#f4f6fb', fontFamily: "'DM Sans', 'Noto Sans Tamil', sans-serif" }}
    >
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 mt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <div className="flex-shrink-0">
            <h1 className="text-gray-900 font-bold text-base leading-tight">
              {language === 'ta' ? 'எங்கள் தயாரிப்புகள்' : 'Our Products'}
            </h1>
            {!loading && (
              <p className="text-gray-400 text-xs mt-0.5">
                {filtered.length} {language === 'ta' ? 'பொருட்கள்' : `item${filtered.length !== 1 ? 's' : ''} available`}
              </p>
            )}
          </div>

          <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

          {/* Search */}
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={language === 'ta' ? 'தயாரிப்புகளை தேடுங்கள்...' : 'Search products...'}
              className="w-full pl-9 pr-8 py-2 text-sm text-gray-800 bg-gray-50 focus:bg-white transition-colors"
              style={{ border: '1.5px solid #e5e7eb', borderRadius: '6px', outline: 'none' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-28 text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
              style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}
            >
              <PackageSearch size={36} className="text-blue-300" />
            </div>
            <p className="text-gray-700 font-bold text-lg mb-1">
              {language === 'ta' ? 'பொருட்கள் எதுவும் கிடைக்கவில்லை' : 'No products found'}
            </p>
            <p className="text-gray-400 text-sm">
              {language === 'ta' ? 'வேறு வார்த்தை முயற்சிக்கவும்.' : 'Try a different keyword.'}
            </p>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="mt-4 px-4 py-2 text-blue-600 font-semibold text-sm hover:bg-blue-50 transition-colors"
                style={{ border: '1.5px solid #bfdbfe', borderRadius: '6px' }}
              >
                {language === 'ta' ? 'தேடலை நீக்கு' : 'Clear search'}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filtered.map((product, i) => (
              <div
                key={product.id}
                style={{
                  animation: 'cardIn 0.3s ease both',
                  animationDelay: `${Math.min(i * 0.03, 0.45)}s`,
                }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Noto+Sans+Tamil:wght@400;600;700&display=swap');
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ProductsListing;